import { useCallback, useRef } from 'react';

const ITEM_SELECTOR = '[role="listitem"][tabindex="0"]';

export function useGridNavigation<T extends HTMLElement>(): (node: T | null) => void {
  const cleanupRef = useRef<(() => void) | null>(null);
  return useCallback((node: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!node) return;
    const onKey = (e: KeyboardEvent) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
      if (items.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      let idx = active ? items.indexOf(active) : -1;
      if (idx === -1) idx = 0;
      let next = idx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % items.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + items.length) % items.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = items.length - 1;
      if (next !== idx) {
        e.preventDefault();
        items[next]!.focus();
      }
    };
    node.addEventListener('keydown', onKey);
    cleanupRef.current = () => node.removeEventListener('keydown', onKey);
  }, []);
}
